import { type MouseEvent, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Layout from '@/components/Layout';
import { HireClippersContactStep } from '@/components/hire-clippers/HireClippersContactStep';
import { HireClippersOptionGrid } from '@/components/hire-clippers/HireClippersOptionGrid';
import { HireClippersSuccess } from '@/components/hire-clippers/HireClippersSuccess';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { trackEvent } from '@/lib/analytics/trackEvent';
import {
  type BrandLeadInputError,
  buildBrandLeadInput,
} from '@/lib/brandLeads/buildBrandLeadInput';
import {
  applyProfileToDraft,
  EMPTY_HIRE_CLIPPERS_DRAFT,
  HIRE_CLIPPERS_STEPS,
  type HireClippersDraft,
  type HireClippersProfile,
  type SingleChoiceStep,
  toggleContentType,
} from '@/lib/brandLeads/hireClippersDraft';
import {
  BUDGET_OPTIONS,
  CONTENT_TYPE_OPTIONS,
  DEMAND_TYPE_OPTIONS,
  type HireClippersOption,
  OBJECTIVE_OPTIONS,
  PLATFORM_OPTIONS,
} from '@/lib/brandLeads/hireClippersOptions';
import { resolveHireClippersSource } from '@/lib/brandLeads/hireClippersRoute';
import {
  CREATE_BRAND_LEAD_FROM_APP,
  type CreateBrandLeadFromAppData,
  type CreateBrandLeadFromAppVars,
} from '@/queries/brandLeads';
import { GET_MY_DATA } from '@/queries/queries';

const SINGLE_CHOICE_OPTIONS: Record<SingleChoiceStep, readonly HireClippersOption[]> = {
  demandType: DEMAND_TYPE_OPTIONS,
  objective: OBJECTIVE_OPTIONS,
  platforms: PLATFORM_OPTIONS,
  budget: BUDGET_OPTIONS,
};

export default function HireClippersPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const source = resolveHireClippersSource(location.search);
  const { data: myDataResult } = useQuery(GET_MY_DATA);
  const [createBrandLead] = useMutation<
    CreateBrandLeadFromAppData,
    CreateBrandLeadFromAppVars
  >(CREATE_BRAND_LEAD_FROM_APP);

  const [draft, setDraft] = useState<HireClippersDraft>(EMPTY_HIRE_CLIPPERS_DRAFT);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepError, setStepError] = useState<BrandLeadInputError | null>(null);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const submittingRef = useRef(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const firstStepRenderRef = useRef(true);

  const profile = (myDataResult?.myData ?? null) as HireClippersProfile | null;
  useEffect(() => {
    if (profile) setDraft((current) => applyProfileToDraft(current, profile));
  }, [profile]);

  useEffect(() => {
    if (firstStepRenderRef.current) {
      firstStepRenderRef.current = false;
      return;
    }
    headingRef.current?.focus({ preventScroll: true });
  }, [stepIndex]);

  const step = HIRE_CLIPPERS_STEPS[stepIndex];
  const isLastStep = stepIndex === HIRE_CLIPPERS_STEPS.length - 1;

  const update = (next: HireClippersDraft) => {
    setDraft(next);
    setStepError(null);
    setSubmitFailed(false);
  };

  // Continuar and Enviar sit in the same spot, so the second click of a
  // double click would land on the next step's button. Keyboard activation
  // has detail 0.
  const isRepeatClick = (event: MouseEvent<HTMLButtonElement>) => event.detail > 1;

  const goNext = () => {
    const result = buildBrandLeadInput(draft, source);
    if (result.ok === false && HIRE_CLIPPERS_STEPS.indexOf(result.step) <= stepIndex) {
      setStepError(result.error);
      return;
    }
    setStepError(null);
    setStepIndex(Math.min(stepIndex + 1, HIRE_CLIPPERS_STEPS.length - 1));
  };

  const goBack = () => {
    setStepError(null);
    setSubmitFailed(false);
    setStepIndex(Math.max(stepIndex - 1, 0));
  };

  const submit = async () => {
    if (submittingRef.current) return;
    const result = buildBrandLeadInput(draft, source);
    if (result.ok === false) {
      setStepIndex(HIRE_CLIPPERS_STEPS.indexOf(result.step));
      setStepError(result.error);
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setSubmitFailed(false);
    try {
      const response = await createBrandLead({ variables: { data: result.input } });
      if (!response.data?.createBrandLead?.documentId) {
        throw new Error('createBrandLead returned no lead');
      }
      trackEvent('brand_lead_success', { source });
      setDone(true);
    } catch (error) {
      console.warn('Unable to create brand lead', error);
      trackEvent('brand_lead_error', { source });
      setSubmitFailed(true);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  let body;
  if (step === 'contentType') {
    body = (
      <HireClippersOptionGrid
        group="contentType"
        options={CONTENT_TYPE_OPTIONS}
        selected={draft.contentType}
        onToggle={(value) => update(toggleContentType(draft, value))}
      />
    );
  } else if (step === 'contact') {
    body = (
      <HireClippersContactStep
        draft={draft}
        disabled={submitting}
        error={stepError}
        onChange={(patch) => update({ ...draft, ...patch })}
      />
    );
  } else {
    body = (
      <HireClippersOptionGrid
        group={step}
        options={SINGLE_CHOICE_OPTIONS[step]}
        selected={[draft[step]]}
        onToggle={(value) => update({ ...draft, [step]: value })}
      />
    );
  }

  return (
    <Layout>
      <div className="mx-auto w-full max-w-3xl space-y-6 px-4 pb-28 pt-6 sm:py-10">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            {t('hireClippers.title')}
          </h1>
          <p className="text-sm text-muted-foreground sm:text-base">
            {t('hireClippers.subtitle')}
          </p>
        </header>

        {done ? (
          <HireClippersSuccess />
        ) : (
          // Contact data and brand answers stay out of Sentry session replays.
          <Card data-sentry-block="">
            <CardContent className="space-y-6 p-5 sm:p-8">
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">
                  {t('hireClippers.stepCounter', {
                    current: stepIndex + 1,
                    total: HIRE_CLIPPERS_STEPS.length,
                  })}
                </p>
                <Progress
                  value={((stepIndex + 1) / HIRE_CLIPPERS_STEPS.length) * 100}
                  className="h-1"
                />
              </div>

              <div className="space-y-1">
                <h2
                  ref={headingRef}
                  tabIndex={-1}
                  className="text-xl font-semibold focus:outline-none sm:text-2xl"
                >
                  {t(`hireClippers.steps.${step}.title`)}
                </h2>
                {step === 'contentType' && (
                  <p className="text-sm text-muted-foreground">
                    {t('hireClippers.steps.contentType.hint')}
                  </p>
                )}
              </div>

              {body}

              {stepError && (
                <p role="alert" className="text-sm text-destructive">
                  {t(`hireClippers.errors.${stepError}`)}
                </p>
              )}
              {submitFailed && (
                <p role="alert" className="text-sm text-destructive">
                  {t('hireClippers.errors.submit')}
                </p>
              )}

              <div className="flex items-center justify-between gap-3">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="hire-clippers-back"
                  onClick={goBack}
                  disabled={stepIndex === 0 || submitting}
                >
                  <ArrowLeft aria-hidden="true" className="h-4 w-4" />
                  {t('hireClippers.actions.back')}
                </Button>
                {isLastStep ? (
                  <Button
                    type="button"
                    data-testid="hire-clippers-submit"
                    onClick={(event) => {
                      if (isRepeatClick(event)) return;
                      void submit();
                    }}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                        {t('hireClippers.actions.submitting')}
                      </>
                    ) : (
                      <>
                        {t('hireClippers.actions.submit')}
                        <ArrowRight aria-hidden="true" className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    data-testid="hire-clippers-next"
                    onClick={(event) => {
                      if (isRepeatClick(event)) return;
                      goNext();
                    }}
                  >
                    {t('hireClippers.actions.next')}
                    <ArrowRight aria-hidden="true" className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
